import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const ArrayOperationsContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.header`
  text-align: center;
  color: white;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin: 10px 0 0 0;
  opacity: 0.9;
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  text-decoration: none;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const MainContent = styled.main`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ArrayContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  color: white;
`;

const ArrayTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  text-align: center;
`;

const ArrayVisualization = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 30px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 400px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  position: relative;
`;

const ArrayBar = styled.div`
  background: ${props => {
    if (props.isHighlighted) return '#ff6b6b';
    if (props.isInserting) return '#4ecdc4';
    if (props.isDeleting) return '#ffd43b';
    if (props.isTraversing) return '#51cf66';
    return '#f8f9fa';
  }};
  width: ${props => Math.max(20, 800 / Math.max(props.arrayLength, 1))}px;
  height: ${props => props.value * 3}px;
  border-radius: 4px 4px 0 0;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const BarValue = styled.div`
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${ArrayBar}:hover & {
    opacity: 1;
  }
`;

const IndexLabel = styled.div`
  position: absolute;
  bottom: -25px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 14px;
`;

const LegendColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.color};
`;

const Controls = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  color: white;
`;

const ControlGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  background: ${props => props.primary ? '#51cf66' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 10px;
  margin-bottom: 10px;
  
  &:hover:not(:disabled) {
    background: ${props => props.primary ? '#40c057' : 'rgba(255, 255, 255, 0.3)'};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AlgorithmInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  color: white;
  height: fit-content;
`;

const InfoTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  text-align: center;
`;

const InfoText = styled.p`
  margin: 0 0 15px 0;
  line-height: 1.6;
  opacity: 0.9;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 20px;
`;

const StatBox = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
`;

const OperationStatus = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
  text-align: center;
  color: ${props => props.success ? '#51cf66' : props => props.error ? '#ff6b6b' : 'white'};
  font-weight: 500;
`;

function ArrayOperationsPage() {
  const [array, setArray] = useState([25, 42, 18, 33, 67, 91, 14, 29, 55, 78]);
  const [arraySize, setArraySize] = useState(10);
  const [insertValue, setInsertValue] = useState('');
  const [insertIndex, setInsertIndex] = useState('');
  const [deleteIndex, setDeleteIndex] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [currentOperation, setCurrentOperation] = useState('');
  const [isOperating, setIsOperating] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [operationSpeed, setOperationSpeed] = useState(500);
  const [operationCount, setOperationCount] = useState(0);
  const [lastOperation, setLastOperation] = useState('');

  const generateRandomArray = (size, min, max) => {
    return Array.from({ length: size }, () => Math.floor(Math.random() * (max - min + 1)) + min);
  };

  const generateNewArray = useCallback(() => {
    const newArray = generateRandomArray(arraySize, 5, 100);
    setArray(newArray);
    resetOperation();
  }, [arraySize]);

  useEffect(() => {
    generateNewArray();
  }, [generateNewArray]);

  const resetOperation = () => {
    setHighlightedIndex(-1);
    setIsOperating(false);
    setCurrentOperation('');
    setOperationCount(0);
    setLastOperation('');
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const insertElement = async () => {
    if (isOperating || !insertValue || insertIndex === '') return;
    
    const value = parseInt(insertValue);
    const index = parseInt(insertIndex);
    
    if (index < 0 || index > array.length) {
      setLastOperation('Error: Invalid index');
      return;
    }
    
    setIsOperating(true);
    setCurrentOperation('Inserting...');
    setOperationCount(prev => prev + 1);
    
    // Create new array with insertion
    const newArray = [...array];
    newArray.splice(index, 0, value);
    
    // Animate insertion
    setHighlightedIndex(index);
    setArray(newArray);
    
    await delay(1000 / operationSpeed);
    
    setLastOperation(`Inserted ${value} at index ${index}`);
    resetOperation();
  };

  const deleteElement = async () => {
    if (isOperating || deleteIndex === '') return;
    
    const index = parseInt(deleteIndex);
    
    if (index < 0 || index >= array.length) {
      setLastOperation('Error: Invalid index');
      return;
    }
    
    setIsOperating(true);
    setCurrentOperation('Deleting...');
    setOperationCount(prev => prev + 1);
    
    const deletedValue = array[index];
    
    // Animate deletion
    setHighlightedIndex(index);
    await delay(1000 / operationSpeed);
    
    // Remove element
    const newArray = array.filter((_, i) => i !== index);
    setArray(newArray);
    
    setLastOperation(`Deleted ${deletedValue} from index ${index}`);
    resetOperation();
  };

  const searchElement = async () => {
    if (isOperating || !searchValue) return;
    
    const targetValue = parseInt(searchValue);
    setIsOperating(true);
    setCurrentOperation('Searching...');
    setOperationCount(prev => prev + 1);
    
    // Linear search with animation
    for (let i = 0; i < array.length; i++) {
      setHighlightedIndex(i);
      await delay(1000 / operationSpeed);
      
      if (array[i] === targetValue) {
        setLastOperation(`Found ${targetValue} at index ${i}`);
        resetOperation();
        return;
      }
    }
    
    setLastOperation(`${targetValue} not found in array`);
    resetOperation();
  };

  const traverseArray = async () => {
    if (isOperating) return;
    
    setIsOperating(true);
    setCurrentOperation('Traversing...');
    setOperationCount(prev => prev + 1);
    
    // Animate through each element
    for (let i = 0; i < array.length; i++) {
      setHighlightedIndex(i);
      await delay(1000 / operationSpeed);
    }
    
    setLastOperation('Array traversal completed');
    resetOperation();
  };

  const reverseArray = async () => {
    if (isOperating) return;
    
    setIsOperating(true);
    setCurrentOperation('Reversing...');
    setOperationCount(prev => prev + 1);
    
    const newArray = [...array].reverse();
    
    // Animate reversal
    for (let i = 0; i < array.length / 2; i++) {
      setHighlightedIndex(i);
      await delay(1000 / operationSpeed);
    }
    
    setArray(newArray);
    setLastOperation('Array reversed successfully');
    resetOperation();
  };

  const sortArray = async () => {
    if (isOperating) return;
    
    setIsOperating(true);
    setCurrentOperation('Sorting...');
    setOperationCount(prev => prev + 1);
    
    const newArray = [...array].sort((a, b) => a - b);
    
    // Animate sorting
    for (let i = 0; i < array.length; i++) {
      setHighlightedIndex(i);
      await delay(1000 / operationSpeed);
    }
    
    setArray(newArray);
    setLastOperation('Array sorted successfully');
    resetOperation();
  };

  return (
    <ArrayOperationsContainer>
      <BackButton to="/">← Back to Home</BackButton>
      
      <Header>
        <Title>Array Operations Visualizer</Title>
        <Subtitle>Learn and visualize various array operations with interactive animations</Subtitle>
      </Header>
      
      <MainContent>
        <LeftPanel>
          <ArrayContainer>
            <ArrayTitle>Array Visualization</ArrayTitle>
            <ArrayVisualization>
              {array.map((value, index) => (
                <ArrayBar
                  key={index}
                  value={value}
                  arrayLength={array.length}
                  isHighlighted={index === highlightedIndex}
                  isInserting={currentOperation === 'Inserting...' && index === highlightedIndex}
                  isDeleting={currentOperation === 'Deleting...' && index === highlightedIndex}
                  isTraversing={currentOperation === 'Traversing...' && index === highlightedIndex}
                >
                  <BarValue>{value}</BarValue>
                  <IndexLabel>{index}</IndexLabel>
                </ArrayBar>
              ))}
            </ArrayVisualization>
            
            <Legend>
              <LegendItem>
                <LegendColor color="#f8f9fa" />
                <span>Normal</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ff6b6b" />
                <span>Highlighted</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#4ecdc4" />
                <span>Inserting</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ffd43b" />
                <span>Deleting</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#51cf66" />
                <span>Traversing</span>
              </LegendItem>
            </Legend>
          </ArrayContainer>
          
          <Controls>
            <ControlGroup>
              <Label>Array Size:</Label>
              <Input
                type="range"
                min="5"
                max="20"
                value={arraySize}
                onChange={(e) => setArraySize(parseInt(e.target.value))}
                disabled={isOperating}
              />
              <div style={{ textAlign: 'center', marginTop: '5px' }}>{arraySize}</div>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Operation Speed:</Label>
              <Input
                type="range"
                min="100"
                max="1000"
                value={operationSpeed}
                onChange={(e) => setOperationSpeed(parseInt(e.target.value))}
                disabled={isOperating}
              />
              <div style={{ textAlign: 'center', marginTop: '5px' }}>{operationSpeed}ms</div>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Insert Element:</Label>
              <Input
                type="number"
                value={insertValue}
                onChange={(e) => setInsertValue(e.target.value)}
                placeholder="Value"
                disabled={isOperating}
                style={{ marginBottom: '10px' }}
              />
              <Input
                type="number"
                value={insertIndex}
                onChange={(e) => setInsertIndex(e.target.value)}
                placeholder="Index"
                disabled={isOperating}
              />
              <Button 
                onClick={insertElement} 
                disabled={isOperating || !insertValue || insertIndex === ''}
                style={{ marginTop: '10px' }}
              >
                Insert
              </Button>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Delete Element:</Label>
              <Input
                type="number"
                value={deleteIndex}
                onChange={(e) => setDeleteIndex(e.target.value)}
                placeholder="Index"
                disabled={isOperating}
              />
              <Button 
                onClick={deleteElement} 
                disabled={isOperating || deleteIndex === ''}
                style={{ marginTop: '10px' }}
              >
                Delete
              </Button>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Search Element:</Label>
              <Input
                type="number"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Value to search"
                disabled={isOperating}
              />
              <Button 
                onClick={searchElement} 
                disabled={isOperating || !searchValue}
                style={{ marginTop: '10px' }}
              >
                Search
              </Button>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Array Operations:</Label>
              <div>
                <Button onClick={traverseArray} disabled={isOperating}>
                  Traverse
                </Button>
                <Button onClick={reverseArray} disabled={isOperating}>
                  Reverse
                </Button>
                <Button onClick={sortArray} disabled={isOperating}>
                  Sort
                </Button>
              </div>
            </ControlGroup>
            
            <ControlGroup>
              <Button onClick={generateNewArray} disabled={isOperating}>
                Generate New Array
              </Button>
            </ControlGroup>
          </Controls>
        </LeftPanel>
        
        <AlgorithmInfo>
          <InfoTitle>Array Operations</InfoTitle>
          
          <InfoText>
            <strong>Current Operation:</strong> {currentOperation || 'Ready'}
          </InfoText>
          
          <InfoText>
            <strong>Array Length:</strong> {array.length}
          </InfoText>
          
          <InfoText>
            <strong>Available Operations:</strong>
          </InfoText>
          
          <InfoText>
            • <strong>Insert:</strong> Add element at specific index
          </InfoText>
          
          <InfoText>
            • <strong>Delete:</strong> Remove element at specific index
          </InfoText>
          
          <InfoText>
            • <strong>Search:</strong> Find element using linear search
          </InfoText>
          
          <InfoText>
            • <strong>Traverse:</strong> Visit each element sequentially
          </InfoText>
          
          <InfoText>
            • <strong>Reverse:</strong> Reverse array order
          </InfoText>
          
          <InfoText>
            • <strong>Sort:</strong> Sort array in ascending order
          </InfoText>
          
          <StatsContainer>
            <StatBox>
              <StatValue>{operationCount}</StatValue>
              <StatLabel>Operations</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{array.length}</StatValue>
              <StatLabel>Elements</StatLabel>
            </StatBox>
          </StatsContainer>
          
          {lastOperation && (
            <OperationStatus 
              success={!lastOperation.includes('Error')}
              error={lastOperation.includes('Error')}
            >
              {lastOperation}
            </OperationStatus>
          )}
        </AlgorithmInfo>
      </MainContent>
    </ArrayOperationsContainer>
  );
}

export default ArrayOperationsPage;
